import { useQuery } from '@apollo/client';
import { BulkTradeRequestSources } from '@generated/graphql';
import { useEffect, useRef, useState } from 'react';
import {
  FETCH_BULK_TRADE_REQUESTS_FOR_RUN,
  FETCH_SUB_TRADE_REQUESTS_FOR_RUN,
} from '../queries/tradeReview.queries';
import type { ReviewTradeRequest } from '../types/tradeReview.types';
import {
  FILTER_FULL_FETCH_MAX,
  OPTIMIZER_POLL_INTERVAL_MS,
  REVIEW_EMPTY_BTR_POLL_WINDOW_MS,
  REVIEW_EMPTY_STR_POLL_WINDOW_MS,
  resolveReviewTradeSettleConfirmMs,
} from '../utils/detailPage/constants';
import {
  areBulkTradeRequestsResolved,
  areReviewTradesFullyFetched,
  areReviewTradesSettled,
  mergeSubTradeRequestPages,
  selectReviewBulkTradeRequest,
  selectUsableBulkTradeRequest,
  shouldPollReviewSubTradeRequests,
  shouldStopReviewTradePolling,
} from '../utils/tradeReview/tradeReviewPresentation';
import { useEmptyPollWindowAge } from './useEmptyPollWindowAge';

export type UseReviewTradePagingParams = {
  showReview: boolean;
  runId: string | undefined;
  organizationId: string | undefined;
};

export type UseReviewTradePagingResult = {
  bulkTradeRequestId: string | undefined;
  usableBulkTradeRequestId: string | undefined;
  noReviewBulkTradeRequest: boolean;
  bulkTradeRequestLoading: boolean;
  trades: ReviewTradeRequest[];
  totalCount: number;
  tradesQueryReady: boolean;
  tradesFullyFetched: boolean;
  tradesSettled: boolean;
  pagingTrades: boolean;
  tradesQueryLoading: boolean;
  refetchTrades: () => Promise<unknown>;
};

export const useReviewTradePaging = ({
  showReview,
  runId,
  organizationId,
}: UseReviewTradePagingParams): UseReviewTradePagingResult => {
  const pagingTradesRef = useRef(false);
  const lastTradePageOffsetRef = useRef<number | null>(null);
  const pageRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [pagingTrades, setPagingTrades] = useState(false);
  const [pagingAttempt, setPagingAttempt] = useState(0);
  const [totalCountConfirmed, setTotalCountConfirmed] = useState(false);
  const [btrPollingStopped, setBtrPollingStopped] = useState(false);
  const [tradesResolved, setTradesResolved] = useState(false);
  const [pollTrades, setPollTrades] = useState(false);

  const { data: bulkTradeRequestData, loading: bulkTradeRequestLoading } =
    useQuery(FETCH_BULK_TRADE_REQUESTS_FOR_RUN, {
      skip: !showReview || !runId,
      fetchPolicy: 'network-only',
      pollInterval:
        showReview && !btrPollingStopped ? OPTIMIZER_POLL_INTERVAL_MS : 0,
      variables: {
        input: {
          filter: {
            source: BulkTradeRequestSources.PortfolioOptimizer,
            sourceId: runId,
          },
          pagination: {
            perPage: FILTER_FULL_FETCH_MAX,
            offSet: 0,
            sortField: 'createdAt',
            sortDesc: true,
          },
        },
      },
    });

  const bulkTradeRequests =
    bulkTradeRequestData?.fetchBulkTradeRequests?.bulkTradeRequests ?? [];
  const bulkTradeRequestTotalCount =
    bulkTradeRequestData?.fetchBulkTradeRequests?.totalCount ?? 0;
  const usableBulkTradeRequest = selectUsableBulkTradeRequest(
    bulkTradeRequests,
    runId,
  );
  const reviewBulkTradeRequest = selectReviewBulkTradeRequest(
    bulkTradeRequests,
    runId,
  );
  const bulkTradeRequestId = reviewBulkTradeRequest?.id;
  const usableBulkTradeRequestId = usableBulkTradeRequest?.id;
  const isEmptyBtrSnapshot =
    Boolean(bulkTradeRequestData) &&
    bulkTradeRequestTotalCount === 0 &&
    !reviewBulkTradeRequest;
  const emptyBtrSnapshotAgeMs = useEmptyPollWindowAge(
    isEmptyBtrSnapshot,
    REVIEW_EMPTY_BTR_POLL_WINDOW_MS,
    runId,
  );
  const bulkTradeRequestsReady = areBulkTradeRequestsResolved({
    queryReady: Boolean(bulkTradeRequestData),
    fetchedCount: bulkTradeRequests.length,
    totalCount: bulkTradeRequestTotalCount,
    hasUsable: Boolean(reviewBulkTradeRequest),
    emptySnapshotAgeMs: emptyBtrSnapshotAgeMs,
    emptyPollWindowMs: REVIEW_EMPTY_BTR_POLL_WINDOW_MS,
  });
  const noReviewBulkTradeRequest =
    bulkTradeRequestsReady && !bulkTradeRequestId;

  const {
    data: tradesData,
    loading: tradesQueryLoading,
    refetch: refetchTrades,
    fetchMore: fetchMoreTrades,
  } = useQuery(FETCH_SUB_TRADE_REQUESTS_FOR_RUN, {
    skip: !showReview || !bulkTradeRequestId || !organizationId,
    fetchPolicy: 'network-only',
    pollInterval:
      showReview && bulkTradeRequestId && pollTrades
        ? OPTIMIZER_POLL_INTERVAL_MS
        : 0,
    variables: {
      input: {
        filter: {
          organizationId,
          bulkTradeRequestId,
        },
        pagination: {
          perPage: FILTER_FULL_FETCH_MAX,
          offSet: 0,
          sortField: 'createdAt',
          sortDesc: false,
        },
      },
    },
  });

  const trades: ReviewTradeRequest[] = bulkTradeRequestId
    ? (tradesData?.fetchSubTradeRequests?.subTradeRequests ?? [])
    : [];
  const totalCount = bulkTradeRequestId
    ? (tradesData?.fetchSubTradeRequests?.totalCount ?? 0)
    : 0;
  const tradesQueryReady = Boolean(bulkTradeRequestId && tradesData);
  const tradesFullyFetched = areReviewTradesFullyFetched(
    trades.length,
    totalCount,
  );
  const isEmptyStrSnapshot =
    tradesQueryReady && totalCount === 0 && trades.length === 0;
  const emptySnapshotAgeMs = useEmptyPollWindowAge(
    isEmptyStrSnapshot,
    REVIEW_EMPTY_STR_POLL_WINDOW_MS,
    runId,
  );

  const tradesSettled =
    noReviewBulkTradeRequest ||
    areReviewTradesSettled({
      tradesQueryReady,
      fetchedCount: trades.length,
      totalCount,
      totalCountConfirmed,
    });

  const stopReviewPolling = shouldStopReviewTradePolling({
    noReviewBulkTradeRequest,
    tradesSettled,
    totalCount,
    emptySnapshotAgeMs,
    emptyPollWindowMs: REVIEW_EMPTY_STR_POLL_WINDOW_MS,
  });
  const shouldPollTrades = shouldPollReviewSubTradeRequests({
    showReview,
    hasBulkTradeRequest: Boolean(bulkTradeRequestId),
    tradesResolved,
    fetchedCount: trades.length,
    totalCount,
    pageSize: FILTER_FULL_FETCH_MAX,
  });

  useEffect(() => {
    pagingTradesRef.current = false;
    lastTradePageOffsetRef.current = null;
    if (pageRetryTimeoutRef.current) {
      clearTimeout(pageRetryTimeoutRef.current);
      pageRetryTimeoutRef.current = null;
    }
    setBtrPollingStopped(false);
    setTradesResolved(false);
    setPollTrades(false);
    setPagingTrades(false);
    setPagingAttempt(0);
    setTotalCountConfirmed(false);
  }, [runId]);

  useEffect(() => {
    if (bulkTradeRequestsReady) {
      setBtrPollingStopped(true);
    }
  }, [bulkTradeRequestsReady]);

  useEffect(() => {
    if (stopReviewPolling) {
      setTradesResolved(true);
    }
  }, [stopReviewPolling]);

  useEffect(() => {
    setPollTrades(shouldPollTrades);
  }, [shouldPollTrades]);

  useEffect(() => {
    if (!tradesQueryReady || !tradesFullyFetched) {
      setTotalCountConfirmed(false);
      return;
    }

    setTotalCountConfirmed(false);
    const timeoutId = window.setTimeout(() => {
      setTotalCountConfirmed(true);
    }, resolveReviewTradeSettleConfirmMs());

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [totalCount, tradesFullyFetched, tradesQueryReady]);

  useEffect(() => {
    if (
      lastTradePageOffsetRef.current != null &&
      lastTradePageOffsetRef.current > trades.length
    ) {
      lastTradePageOffsetRef.current = null;
    }

    if (
      !bulkTradeRequestId ||
      !organizationId ||
      pagingTradesRef.current ||
      trades.length >= totalCount ||
      lastTradePageOffsetRef.current === trades.length
    ) {
      return;
    }

    const pageOffset = trades.length;
    pagingTradesRef.current = true;
    setPagingTrades(true);

    void fetchMoreTrades({
      variables: {
        input: {
          filter: {
            organizationId,
            bulkTradeRequestId,
          },
          pagination: {
            perPage: FILTER_FULL_FETCH_MAX,
            offSet: pageOffset,
            sortField: 'createdAt',
            sortDesc: false,
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousPage = previousResult.fetchSubTradeRequests;
        const incomingPage = fetchMoreResult?.fetchSubTradeRequests;
        const incomingRows = incomingPage?.subTradeRequests ?? [];

        if (incomingRows.length === 0) {
          lastTradePageOffsetRef.current = pageOffset;
        }

        return {
          fetchSubTradeRequests: {
            ...(incomingPage ?? previousPage),
            subTradeRequests: mergeSubTradeRequestPages(
              previousPage?.subTradeRequests ?? [],
              incomingRows,
            ),
            totalCount:
              incomingPage?.totalCount ?? previousPage?.totalCount ?? 0,
          },
        };
      },
    })
      .catch(() => {
        lastTradePageOffsetRef.current = null;
        if (pageRetryTimeoutRef.current) {
          clearTimeout(pageRetryTimeoutRef.current);
        }
        pageRetryTimeoutRef.current = setTimeout(() => {
          setPagingAttempt((attempt) => attempt + 1);
        }, OPTIMIZER_POLL_INTERVAL_MS);
      })
      .finally(() => {
        pagingTradesRef.current = false;
        setPagingTrades(false);
      });
  }, [
    bulkTradeRequestId,
    fetchMoreTrades,
    organizationId,
    pagingAttempt,
    totalCount,
    trades.length,
  ]);

  return {
    bulkTradeRequestId,
    usableBulkTradeRequestId,
    noReviewBulkTradeRequest,
    bulkTradeRequestLoading,
    trades,
    totalCount,
    tradesQueryReady,
    tradesFullyFetched,
    tradesSettled,
    pagingTrades,
    tradesQueryLoading,
    refetchTrades,
  };
};
